import { useChatStatus, type UseChatStatusArgs } from "./useChatStatus";

export type UseChatVisibilityArgs = UseChatStatusArgs & {
  /** Optional local override to disable chat (e.g., from props or feature flag) */
  isChatDisabled?: boolean;
};

export type UseChatVisibilityResult = {
  /** Whether chat should be shown (considering all factors) */
  shouldShowChat: boolean;
  /** Whether we're still loading TalkJS data */
  isLoading: boolean;
  /** Whether chat is enabled based on server config */
  isChatEnabled: boolean;
  /** Whether chat is disabled via local override */
  isChatDisabled: boolean;
};

/**
 * Centralizes chat visibility logic.
 *
 * Determines whether chat features should be visible based on:
 * 1. The backend chat system variable (on/off/exclude/include)
 * 2. User's circle membership
 * 3. Optional local override (isChatDisabled)
 *
 * @example
 * ```typescript
 * const { shouldShowChat, isLoading } = useChatVisibility({
 *   talkjsData,
 *   userCircles: user?.circles,
 *   isChatDisabled: featureFlags.isChatDisabled,
 * });
 *
 * if (!shouldShowChat) return null;
 * ```
 */
export function useChatVisibility({
  talkjsData,
  userCircles,
  isChatDisabled = false,
}: UseChatVisibilityArgs): UseChatVisibilityResult {
  const { isChatEnabled, isLoading } = useChatStatus({ talkjsData, userCircles });

  // Determine if chat should be visible
  // If there's a local override (isChatDisabled), respect it
  // Otherwise, use the system-wide chat status
  const shouldShowChat = isChatDisabled ? false : isChatEnabled;

  return {
    shouldShowChat,
    isLoading,
    isChatEnabled,
    isChatDisabled,
  };
}

export default useChatVisibility;

